import { RefObject, useCallback, useLayoutEffect, useRef } from "react";

import { EASE_SMOOTH_OUT, FRAME_SLACK_MS, prefersReducedMotion, REFLOW_MS } from "./motion";

// FLIP for rows (and the controls below or beside them) that move because a
// sibling left: they glide into the freed space with a transform instead of
// snapping. Animating the collapse itself (height / margin / grid-rows) is off the
// table under the compositor-only rule, so this is the transform-only substitute.
//
//   capture()  First: record every `[data-flip-id]` rect under `root`. Call it
//              right before the state update that removes something.
//   (effect)   Last → Invert → Play on the next commit: jump each moved element
//              back to where it was with a translate3d, then transition it to 0.
//
// It's opt-in per change: only callers that know a removal is coming capture, so
// unrelated re-renders (poller refetches, Load more) never animate. A capture that
// no commit consumes (the mutation was a no-op) goes stale and is ignored.
//
// Same hygiene as useSwipeToDismiss: inline styles driven off refs (no re-render
// per frame), `will-change` set at the start and cleared on transitionend (timer
// fallback), and an early return under reduced motion.

const STALE_MS = 250;

export const useFlip = (root: RefObject<HTMLElement>) => {
  const first = useRef<{ at: number; rects: Map<string, DOMRect> } | null>(null);
  // Per-element cleanup for an in-flight glide, so a new one can take over cleanly.
  const pending = useRef(new WeakMap<HTMLElement, () => void>());

  const capture = useCallback(() => {
    const container = root.current;
    if (!container || prefersReducedMotion) {
      return;
    }
    const rects = new Map<string, DOMRect>();
    container.querySelectorAll<HTMLElement>("[data-flip-id]").forEach((el) => {
      // Includes any in-flight glide offset, i.e. where the element is *seen*.
      rects.set(el.dataset.flipId as string, el.getBoundingClientRect());
    });
    first.current = { at: performance.now(), rects };
  }, [root]);

  // No deps: runs after every commit, and is a no-op unless a capture is waiting.
  useLayoutEffect(() => {
    const snapshot = first.current;
    if (!snapshot) {
      return;
    }
    first.current = null;
    const container = root.current;
    if (!container || performance.now() - snapshot.at > STALE_MS) {
      return;
    }

    const targets = Array.from(container.querySelectorAll<HTMLElement>("[data-flip-id]")).filter(
      // Rows that are mid enter/exit are driven by CSS animations, which override
      // inline transforms anyway (and held off-screen ghosts are about to go).
      (el) => !el.matches(".notif-entering, .notif-exiting, .notif-exit-hidden")
    );

    // Writes: settle any in-flight glide so the reads below see true layout positions.
    targets.forEach((el) => {
      pending.current.get(el)?.();
    });
    // Reads, one batch (no writes in between, so this is a single layout).
    const viewport = container.getBoundingClientRect();
    const measured = targets.map((el) => ({ el, after: el.getBoundingClientRect() }));

    const moves: { el: HTMLElement; dx: number; dy: number }[] = [];
    measured.forEach(({ el, after }) => {
      const before = snapshot.rects.get(el.dataset.flipId as string);
      if (!before) {
        return;
      }
      const dx = before.left - after.left;
      const dy = before.top - after.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
        return;
      }
      // Whole path outside the panel (scrolled away): nothing to see, so don't
      // promote a layer for it. It just takes its new place.
      const top = Math.min(before.top, after.top);
      const bottom = Math.max(before.bottom, after.bottom);
      if (bottom <= viewport.top || top >= viewport.bottom) {
        return;
      }
      moves.push({ el, dx, dy });
    });
    if (moves.length === 0) {
      return;
    }

    // Invert: put every moved element back where it was seen, promoted one-shot.
    moves.forEach(({ el, dx, dy }) => {
      el.style.transition = "none";
      el.style.willChange = "transform";
      el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
    });
    // One style flush for the whole batch so the inverted pose is the "from". Only
    // transforms changed since the reads, so this recalcs style, not layout.
    void getComputedStyle(moves[0].el).opacity;

    // Play: release them to their real positions.
    moves.forEach(({ el }) => {
      el.style.transition = `transform ${REFLOW_MS}ms ${EASE_SMOOTH_OUT}`;
      el.style.transform = "translate3d(0, 0, 0)";

      let done = false;
      const onEnd = (event: TransitionEvent) => {
        if (event.target === el && event.propertyName === "transform") {
          finish();
        }
      };
      const finish = () => {
        if (done) {
          return;
        }
        done = true;
        el.removeEventListener("transitionend", onEnd);
        window.clearTimeout(timer);
        pending.current.delete(el);
        el.style.transition = "";
        el.style.transform = "";
        el.style.willChange = "";
      };
      el.addEventListener("transitionend", onEnd);
      const timer = window.setTimeout(finish, REFLOW_MS + FRAME_SLACK_MS);
      pending.current.set(el, finish);
    });
  });

  return capture;
};

export default useFlip;
